import { useState } from 'react'

import { Disclosure, DisclosurePanel } from '@headlessui/react'
import { IconCloudFilled, IconCloudRegular } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import groupBy from 'lodash/groupBy'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { ApiSubNav } from '~/portal/components/ConsumerSideNavigation/ApiNavItem/ApiSubNav'
import { calculateNestedMargin } from '~/portal/components/ConsumerSideNavigation/helpers'
import { SideNavItem } from '~/portal/components/ConsumerSideNavigation/SideNavItem'
import { ApiTocItem } from '~/portal/types/content'
import { getApiTypeFromSpec } from '~/portal/utils/api'

export const ApiNavItem = ({
  item,
  isActive,
  level,
  openIds,
  onClick,
  isMobile,
  flat,
}: {
  item: ApiTocItem,
  isActive: boolean,
  level: number,
  openIds: string[],
  onClick?: (e: any) => void,
  isMobile?: boolean,
  flat?: boolean,
}) => {
  const { t } = useTranslation()
  const groupedOperations = groupBy(item.operations, operation => operation.tag || '')
  const location = useLocation()

  const [selectedItem, setSelectedItem] = useState<string | null>(null)

  const schemas = item.schemas
  const asyncApiMessages = item.asyncApiMessages
  const asyncApiServers = item.asyncApiServers

  const isExpandable = (item.operations && item.operations.length > 0) ||
                       (item.asyncApiMessages && item.asyncApiMessages.length > 0) ||
                       (item.schemas && item.schemas.length > 0)

  const subNavContent = (
    <div className={clsx('sp-sidenav-api-subnav', isActive ? 'bg-(--sp-sidenav-item-active-bg,var(--sp-color-surface-raised)) rounded-b' : 'bg-(--sp-sidenav-item-bg,var(--sp-color-surface))')}>
      {asyncApiServers && asyncApiServers.length > 0 ? <ApiSubNav
        key="servers"
        tag="Servers"
        asyncApiServers={asyncApiServers}
        active={isActive}
        parentPath={`${item.path}${location.search}`}
        level={level}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        onClick={onClick}
      /> : null}
      {Object.keys(groupedOperations).map(tag => (
        <ApiSubNav
          key={tag}
          tag={tag}
          operations={groupedOperations[tag]}
          active={isActive}
          parentPath={`${item.path}${location.search}`}
          level={level}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          onClick={onClick}
        />
      ))}
      {asyncApiMessages && asyncApiMessages.length > 0 ? <ApiSubNav
        key="messages"
        tag="Messages"
        asyncApiMessages={asyncApiMessages}
        active={isActive}
        parentPath={`${item.path}${location.search}`}
        level={level}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        onClick={onClick}
      /> : null}
      {item.schemas && item.schemas.length > 0 ? <ApiSubNav
        key="schemas"
        tag="Schemas"
        schemas={schemas}
        active={isActive}
        parentPath={`${item.path}${location.search}`}
        level={level}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        onClick={onClick}
        apiType={getApiTypeFromSpec(item.apiSpec)}
      /> : null}
      <div className="separator h-1.5" />
    </div>
  )

  if (flat) {
    return (
      <div className="sp-sidenav-api-item">
        <SideNavItem
          path={`${item.path}#top`}
          title={item.title}
          icon={isActive ? <IconCloudFilled /> : <IconCloudRegular height={9} width={12} />}
          isActive={isActive}
          isExpandable={false}
          level={level}
          isApi={true}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          onClick={onClick}
          isMobile={isMobile}
        />
        {subNavContent}
      </div>
    )
  }

  return (
    <Disclosure aria-label={t('consumer.sideNavigation.apiNavItem.ariaLabel')} as="div" className="sp-sidenav-api-item" defaultOpen={openIds.includes(item.tableOfContentsId ?? '')} key={`${item.path ?? ''}-${openIds.join('-')}`}>
      {({ open }) => (
        <>
          <SideNavItem
            path={`${item.path}${location.search}#top`}
            title={item.title}
            icon={isActive ? <IconCloudFilled /> : <IconCloudRegular height={9} width={12} />}
            isActive={isActive}
            isExpandable={isExpandable}
            level={level}
            open={open}
            isApi={true}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            onClick={onClick}
            isMobile={isMobile}
          />
          <DisclosurePanel style={{ paddingLeft: calculateNestedMargin(level, isMobile) }} className={clsx(level > 0 && 'pr-3')}>
            {subNavContent}
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}